import { getSystem } from '../../system';

export const AppAttributes = {
  DEVICE_ID: getSystem().getDeviceId(),
};

export enum UserIdentities {
  EMAIL = 'email',
  CUSTOMER_ID = 'customerid',
  MOBILE_NUMBER = 'mobile_number',
}

export enum UserAttributes {
  CUSTOMER_TYPE = 'customer_type',
  BDU_PROVIDER = 'bdu_provider',
  UM_ACCOUNT_CREATION_DATE = 'um_account_creation_date',
  SUBSCRIPTION_TYPE = 'subscription_type',
  SUBSCRIPTION_PARTNERS = 'subscription_partners',
}

export const CustomEvent = {
  INITIAL_APP_LOADED: ['initial_app_loaded', 2],
  SCREEN_VIEWED: ['screen_viewed', 2],
  CONTAINER_INTERACTION: ['container_interaction', 8],
  RECOMMENDATION_DISPLAYED: ['reco_displayed', 8],
  SEARCH_PERFORMED: ['search_performed', 3],
  SEARCH_RESULT_SELECTED: ['search_result_selected', 3],
  USER_SIGNED_UP: ['user_signed_up', 8],
  USER_SIGNED_IN: ['user_signed_in', 8],
  USER_SIGNED_OUT: ['user_signed_out', 8],
  SUBSCRIPTION_CANCELLED: ['subscription_cancelled', 8],
  CTA_CLICKED: ['cta_clicked', 8],
  LINK_CLICK: ['link_click', 8],
  PROFILE_ADDED: ['profile_added', 6],
  PROFILE_REMOVED: ['profile_removed', 6],
  PROFILE_SWITCHED: ['profile_switched', 6],
  PROFILE_EDITED: ['profile_edited', 6],
  LIST_CONTENT_ADDED: ['list_content_added', 6],
  LIST_CONTENT_REMOVED: ['list_content_removed', 6],
};

export const ContainerInteractionType = {
  PLAY_THROUGH: 'play-through',
  PAGE_SCREEN: 'page/screen',
  CONTAINER_CTA_CLICKED: 'container cta clicked',
  FEATURED_CARD_CTA: 'featured_card_cta',
};

export enum ScreenName {
  LANDING = 'Landing Screen',
  CREATE_ACCOUNT = 'Create Account Screen',
  NAVIGATION = 'Navigation',
  SEARCH = 'Search',
  FRONTDOOR = 'Frontdoor',
  FRONTDOOR_SIGN_IN_SELECTION = 'Frontdoor Sign In Selection',
  SETTINGS = 'Settings',
  MY_CRAVINGS = 'My Cravings',
  SIGN_IN_CRAVE = 'Sign in Through Crave',
  SIGN_IN_PROVIDER = 'Sign in Through Provider',
  SIGN_IN_TV_PROVIDER = 'Sign in Through TV Provider',
  RESET_PASSWORD = 'Reset Password',
  RESET_PASSWORD_CONFIRMATION = 'Reset Password Confirmation',
  SUBSCRIBE = 'Subscribe',
  PRODUCT_INTRO = 'Product Intro',
  GRID = 'Grid Screen',
  SHOW_PAGE = 'Show Page',
  LANGUAGES = 'Languages',
  MORE_INFO = 'More Info',
  SPLASH = 'Splash Screen',
  PROFILE = 'Profile Selection',
  SWITCH_PROFILE = 'Switch Profile',
  SIGN_OUT = 'Sign Out',
  EDIT_PROFILE = 'Edit Profile',
  EDIT_NAME = 'Profile Name',
  ENTER_PIN = 'Add PIN',
  ENTER_PROFILE_PIN = 'Enter PIN',
  DISPLAY_LANGUAGE = 'Display Language',
  PROFILE_DISPLAY_LANGUAGE = 'Display Language',
  AUDIO = 'Content Preferences & Audio/Subtitle Languages',
  CONTENT_ACCESS = 'Content Access',
  ADD_PROFILE = 'Add Profile',
  PLAYER = 'Player',
  SUBTITLE = 'Audio/SubTitles Language',
  PROFILE_AUDIO_SUBTITLES = 'Content Preferences & Audio/Subtitle Languages',
  AUTOPLAY = 'Autoplay',
  FAQS = 'FAQs',
  MANAGE = 'Manage Account',
  CONTACT = 'Contact Us',
  PRIVACY = 'Privacy Policy',
  TERMS = 'Terms & Conditions',
  RATING = 'Rating Information',
  RESET_REMOVE_PIN = 'Reset/Remove Pin',
  WAYSTOWATCH = 'Ways to Watch',
  MANAGE_SUBSCRIPTION = 'Manage Subscription',
  RELINK = 'Sign in Through TV Provider',
  LINK = 'Link Account',
  UNKNOWN = 'Unknown Screen',
  ONBOARDING_LANGUAGE = 'Choose Your Audio/Subtitle Languages',
  ONBOARDING_TITLE_SELECTION = 'Onboarding Title Selection',
  LANGUAGE_SETTINGS = 'Language Settings',
  LIVE_EVENT = 'Live Event',
}

export const containerDisplayTitle = {
  MY_LIST: 'My List',
};

/**
 * Defined public routes - unauthed users can access.
 */
export const publicRoutes = [
  '/frontdoor',
  '/deep-link',
  // Unauthenticated deep links land here for the Sign In CTA / Ways to Watch, and
  // the showpage CTAs gate on auth themselves.
  // Users can't access this page w/o a deeplink. The content for the page is also guarded.
  '/showpage',
  '/onetrust',
  '/product-intro',
  '/signin',
  '/create-account',
  '/subscribe',
];

export const screenMapping: { [key: string]: string } = {
  '/screen': ScreenName.LANDING,
  '/frontdoor/sign-in-selection': ScreenName.FRONTDOOR_SIGN_IN_SELECTION,
  '/frontdoor': ScreenName.FRONTDOOR,
  '/subscribe': ScreenName.SUBSCRIBE,
  '/signin/provider': ScreenName.SIGN_IN_PROVIDER,
  '/signin/crave': ScreenName.SIGN_IN_CRAVE,
  '/signin/reset-password/confirmation': ScreenName.RESET_PASSWORD_CONFIRMATION,
  '/signin/reset-password': ScreenName.RESET_PASSWORD,
  '/grid': ScreenName.GRID,
  '/product-intro': ScreenName.PRODUCT_INTRO,
  search: ScreenName.SEARCH,
  '/myCravings': ScreenName.LANDING,
  '/player': ScreenName.PLAYER,
  '/onboarding/language': ScreenName.ONBOARDING_LANGUAGE,
  '/onboarding/title-selection': ScreenName.ONBOARDING_TITLE_SELECTION,
  '/showpage': ScreenName.SHOW_PAGE,
  '/settings/subtitle': ScreenName.AUDIO,
  '/autoplay': ScreenName.AUTOPLAY,
  '/ways-to-watch/manage-subscription': ScreenName.MANAGE_SUBSCRIPTION,
  '/manage': ScreenName.MANAGE,
  '/faqs': ScreenName.FAQS,
  '/contact': ScreenName.CONTACT,
  '/privacy': ScreenName.PRIVACY,
  '/terms': ScreenName.TERMS,
  '/rating': ScreenName.RATING,
  '/profile/edit/reset-remove-pin': ScreenName.RESET_REMOVE_PIN,
  '/profile/edit/display-language': ScreenName.PROFILE_DISPLAY_LANGUAGE,
  '/profile/edit/audio': ScreenName.PROFILE_AUDIO_SUBTITLES,
  '/settings/language': ScreenName.DISPLAY_LANGUAGE,
  '/language': ScreenName.LANGUAGES,
  '/moreInfo': ScreenName.MORE_INFO,
  '/profile/switch-profile': ScreenName.SWITCH_PROFILE,
  '/sign-out': ScreenName.SIGN_OUT,
  '/settings': ScreenName.SETTINGS,
  '/add': ScreenName.ADD_PROFILE,
  '/name': ScreenName.EDIT_NAME,
  '/enter-pin': ScreenName.ENTER_PIN,
  '/display-language': ScreenName.DISPLAY_LANGUAGE,
  '/audio': ScreenName.AUDIO,
  '/content-access': ScreenName.CONTENT_ACCESS,
  '/profile/edit': ScreenName.EDIT_PROFILE,
  '/profile/edit-profile': ScreenName.EDIT_PROFILE,
  '/profile': ScreenName.PROFILE,
  '/ways-to-watch': ScreenName.WAYSTOWATCH,
  '/ways-to-watch/relink': ScreenName.RELINK,
  '/live-event': ScreenName.LIVE_EVENT,
  '/': ScreenName.SPLASH,
};

export function getPathByScreenName(screenName: ScreenName): string {
  return (
    Object.keys(screenMapping).find((key) => screenMapping[key] === screenName) || ''
  );
}

export enum PLATFORM {
  SAMSUNG = 'tv_samsung',
  LG = 'tv_lg',
  HISENSE = 'tv_hisense',
  XBOX = 'tv_xboxone',
  PS4 = 'tv_ps4',
  PS5 = 'tv_ps5',
}

export const ScreenTypeMap = {
  [ScreenName.LANDING]: 'landing',
  [ScreenName.SEARCH]: 'search',
  [ScreenName.SETTINGS]: 'settings',
  [ScreenName.SIGN_IN_CRAVE]: 'login',
  [ScreenName.SIGN_IN_PROVIDER]: 'login',
  [ScreenName.RESET_PASSWORD]: 'login',
  [ScreenName.RESET_PASSWORD_CONFIRMATION]: 'login',
  [ScreenName.SHOW_PAGE]: 'showpage',
  [ScreenName.LANGUAGES]: 'showpage',
  [ScreenName.MORE_INFO]: 'showpage',
  [ScreenName.WAYSTOWATCH]: 'showpage',
  [ScreenName.PROFILE]: 'profile',
  [ScreenName.SWITCH_PROFILE]: 'profile',
  [ScreenName.EDIT_PROFILE]: 'profile',
  [ScreenName.EDIT_NAME]: 'profile',
  [ScreenName.ENTER_PIN]: 'profile',
  [ScreenName.DISPLAY_LANGUAGE]: 'profile',
  [ScreenName.AUDIO]: 'profile',
  [ScreenName.CONTENT_ACCESS]: 'profile',
  [ScreenName.ADD_PROFILE]: 'profile',
  [ScreenName.SIGN_OUT]: 'logout',
};

export enum ProfileLanguageMapping {
  EN = 'english',
  FR = 'french',
}

export enum ProfilePlaybackLanguageMapping {
  EN = 'english',
  FR = 'french',
}

export type EVENTS_LIST = keyof typeof CustomEvent;

export enum PlatformType {
  Web = 'web',
  iOS = 'ios',
  TVOS = 'tvos',
  Android = 'android',
  Android_TV = 'android_tv',
  Fire_TV = 'fire_tv',
  TV_Samsung = 'tv_samsung',
  Chromecast = 'chromecast',
  TV_XboxOne = 'tv_xboxone',
  XBOX_S = 'xbox_s',
  XBOX_X = 'xbox_x',
  TV_PS4 = 'tv_ps4',
  TV_PS5 = 'tv_ps5',
  ROKU = 'roku',
  TV_LG_WebOS = 'tv_lg_webos',
  Facebook_Portal = 'facebook_portal',
  STB_MediaRoom = 'stb_mediaroom',
  STB_Xfinity = 'stb_xfinity',
  TV_Hisense = 'tv_hisense',
}

export enum PageType {
  LandingPage = 'landingpage',
  MediaPage = 'mediapage',
  LivePage = 'livepage',
  Article = 'article',
  Index = 'index',
  Recipe = 'recipe',
}

export enum ProfileType {
  Adult = 'adult',
  Teen = 'teen',
  OlderKid = 'older kid',
  YoungetKid = 'younger kid',
  Preschool = 'pre school',
}

export enum ListType {
  video = 'video',
}

export enum ListName {
  mylist = 'mylist',
  watchlist = 'watch list',
  favourites = 'favourites',
  newsletter = 'newsletter',
  preferencecentre = 'preference centre',
  onboarding = 'onboarding',
}
